import { useState } from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import AssetDetailSection from './AssetDetailSection';
import CustomButton from '../../../components/CustomButton';
import SelectField from '../../../components/ui/SelectField';
import CustomModal from '../../common/CustomModal';
import {
    invalidateInventoryReaders,
    useConsolidateRoomMutation,
} from '../../../api/inventory/inventoryApiSlice';
import { readInventoryFailure } from '../../../api/inventory/inventoryErrors';
import { enqueueErrorSnackbar } from '../../../action/snackbarCommands';
import type { HouseListItem } from '../../../api/station/stationApiTypes';
import { consolidationRefusalKey, isAdoption } from '../houseConsolidation';
import { makeStationDetailsUrl } from '../../../routes';

interface Props {
    assetId: string;
    /** The houses at exactly this room's address (`housesAtAddress`). */
    houses: HouseListItem[];
    /** The room's own location — a house standing there adopts the room (`isAdoption`). */
    ownLocationId: string | null;
    /** The edit around it holds unsaved changes: save or discard them first. */
    blockedByUnsavedChanges: boolean;
    /** The room is in the house now; the surrounding edit ends. */
    onAssigned: () => void;
}

/**
 * "Dem Haus zuordnen" — plan `room-houses-and-combining-rooms.md` §4 row 3.
 *
 * Shown only on a room that stands on its own automatic one-room house while
 * a real house stands at the identical address. A one-time consolidation, not
 * a move: the room, its bookings, holds, codes and price sheet follow it into
 * the house, and the old location is retired. Confirmed in a dialog that says
 * what happens — including that an on-site QR sticker of the room is replaced
 * by the house's (§6, PO decision) — because it is not undone.
 */
const AssignToHouseSection = ({
    assetId, houses, ownLocationId, blockedByUnsavedChanges, onAssigned,
}: Props) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const [houseId, setHouseId] = useState(houses.length === 1 ? houses[0]!.id : '');
    const [open, setOpen] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [consolidate, { isLoading }] = useConsolidateRoomMutation();
    const house = houses.find((candidate) => candidate.id === houseId);

    const handleConfirm = async () => {
        if (!houseId) return;
        setError(null);
        try {
            const result = await consolidate({ assetId, houseId }).unwrap();
            // Expected 0. A snackbar, not inline text: once the room is in the
            // house this section is no longer shown, and the overlap has to be
            // resolved by hand (the later booking is flagged as a conflict).
            if (result.overlappingBookings > 0) {
                dispatch(enqueueErrorSnackbar({
                    message: t('asset_detail.assign_house.overlap_notice', { count: result.overlappingBookings }),
                    autoHideDuration: 20000,
                }));
            }
            invalidateInventoryReaders(dispatch);
            setOpen(false);
            onAssigned();
        } catch (err) {
            const failure = readInventoryFailure(err);
            const key = consolidationRefusalKey(failure.reason);
            setError(key ? t(key) : failure.message ?? t('asset_detail.assign_house.save_error'));
        }
    };

    return (
        <AssetDetailSection title={t('asset_detail.assign_house.title')}>
            <div className={'flex flex-col gap-3 max-w-xl'}>
                <p className={'text-sm text-foreground/60'}>{t('asset_detail.assign_house.hint')}</p>
                {houses.length > 1 && (
                    <SelectField
                        label={t('asset_detail.assign_house.house_label')}
                        value={houseId}
                        onChange={setHouseId}
                        options={houses.map((candidate) => candidate.id)}
                        optionLabels={Object.fromEntries(houses.map((candidate) => [candidate.id, candidate.name]))}
                        placeholder={t('asset_create.placement.house_placeholder')}
                    />
                )}
                {/* The house's page holds its opening hours, which the room
                    takes on assignment — the only way to see them first. */}
                {house && (
                    <Link to={makeStationDetailsUrl(house.id)} className={'text-sm text-primary hover:underline'}>
                        {t('asset_detail.assign_house.house_link', { house: house.name })}
                    </Link>
                )}
                <div>
                    <CustomButton
                        title={t('asset_detail.assign_house.action')}
                        onClick={() => { setError(null); setOpen(true); }}
                        disabled={!houseId || blockedByUnsavedChanges}
                    />
                </div>
                {blockedByUnsavedChanges && (
                    <p className={'text-sm text-foreground/60'}>
                        {t('asset_detail.assign_house.unsaved_changes')}
                    </p>
                )}
                {error && !open && <p className={'text-sm text-error'}>{error}</p>}
            </div>

            <CustomModal
                open={open}
                onClose={() => setOpen(false)}
                title={t('asset_detail.assign_house.confirm_title', { house: house?.name ?? '' })}
                description={isAdoption(house, ownLocationId)
                    ? t('asset_detail.assign_house.confirm_description_adopt')
                    : t('asset_detail.assign_house.confirm_description')}
                saveButton={
                    <CustomButton
                        title={t('asset_detail.assign_house.confirm')}
                        onClick={handleConfirm}
                        loading={isLoading}
                        disabled={isLoading}
                    />
                }
                closeButton={
                    <CustomButton title={t('common.cancel')} isFilled={false} onClick={() => setOpen(false)} />
                }
            >
                {error && <p className={'text-sm text-error'}>{error}</p>}
            </CustomModal>
        </AssetDetailSection>
    );
};

export default AssignToHouseSection;
